import { getUnstyledAllocationVariantLabel } from '@/components/AllocationVariantIndex/getFullAllocationVariantIndex';
import { TimelineVariant } from '@/components/Timeline/allocation/types';
import { getTransparencyVariant } from '@/components/Timeline/timeline';
import { AllocationType } from '@/components/Timeline/Timeline.types';
import { isArrayEqual } from '@/utils/compare';
import { computed, ref, Ref, toValue } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import {
    ACTIVE_STATES,
    DEFAULT_HIGHLIGHT_VALUE,
    ExistingAllocationVariantEntry,
    SelectionPanelEntries,
} from '../types';

function getLinkTarget(allocationType: AllocationType, objectId: string, index: number): RouteLocationRaw {
    switch (allocationType) {
        case 'Event':
            return { name: 'event-variants-tab', params: { eventId: objectId }, hash: `#v${index}` };
        case 'Project':
            return { name: 'project-variants-tab', params: { projectId: objectId }, hash: `#v${index}` };
        case 'ExternalEvent':
            return { name: 'external-event-event-variants-tab', params: { eventId: objectId }, hash: `#v${index}` };
    }
}

function getVisibleIdsFromIndexes(indexes: number[], variants: TimelineVariant[]) {
    return variants.filter((v) => indexes.includes(v.index)).map((v) => v.id);
}

function getVisibleIndexesFromIds(ids: string[], variants: TimelineVariant[]) {
    return variants.filter((v) => ids.includes(v.id)).map((v) => v.index);
}

function getVariantById(id: string, variants: TimelineVariant[]) {
    return variants.find((v) => v.id === id);
}

function getVariantByIndex(index: number, variants: TimelineVariant[]) {
    return variants.find((v) => v.index === index);
}

export function useVariantSelectionPanel(
    variantsRef: Ref<TimelineVariant[]>,
    setVariantsRef: (value: TimelineVariant[]) => void,
    allocationType: AllocationType,
    objectId: Ref<string>,
    visible: Ref<string>,
    highlight: Ref<string>,
) {
    const visibleVariantIds: Ref<string[]> = ref([]);
    const highlightedVariantId = ref(DEFAULT_HIGHLIGHT_VALUE);
    const numVariants = computed(() => toValue(variantsRef).length);
    const numVisibleVariants = computed(() => toValue(visibleVariantIds).length);
    const defaultActiveVariants = computed(() => toValue(variantsRef).filter((v) => ACTIVE_STATES.includes(v.status)));

    const dropDownStatus = computed(() => {
        if (toValue(numVariants) === 1) {
            if (toValue(numVisibleVariants) === 0) {
                return { status: 'Error' as const, display: 'all-hidden' };
            }

            if (visibleVariantsWithAllocations.value.length === 0) {
                return {
                    status: 'Warning' as const,
                    display: 'warning',
                };
            }
        }

        if (toValue(numVisibleVariants) <= 0) {
            return { status: 'Error' as const, display: 'all-hidden' };
        } else {
            if (visibleVariantsWithAllocations.value.length === 0) {
                return {
                    status: 'Warning' as const,
                    display: 'warning',
                };
            }

            const visibleVariantsAreDefault = isArrayEqual(
                [...visibleVariantIds.value],
                defaultActiveVariants.value.map((v) => v.id),
            );

            if (highlightedVariant.value || !visibleVariantsAreDefault) {
                return { status: 'Active' as const, display: 'highlighted' };
            }
        }

        return { status: 'Default' as const, display: 'all-visible' };
    });

    const currentVariant = computed(() => {
        const variants = toValue(variantsRef);
        if (variants.length <= 0) return;
        const current = variants.find((v) => v.isCurrent === true);
        return current;
    });

    const mappedCurrentVariant = computed(() => {
        const variant = currentVariant.value;
        if (variant) {
            return { id: variant.id, name: variant.name, index: variant.index };
        }
    });

    const highlightedVariant = computed(() => {
        return toValue(variantsRef).find((v) => v.id === toValue(highlightedVariantId));
    });

    // Editing in the timeline changes the highlighted variant.
    // If no variant is highlighted it changes the currentVariant.
    const editableVariant = computed(() => highlightedVariant.value ?? currentVariant.value);

    const editableVariantLabel = computed(() => {
        if (!editableVariant.value) {
            return '';
        }

        return getUnstyledAllocationVariantLabel(editableVariant.value.index, editableVariant.value.name);
    });

    const selectionPanelVariants = computed(() => {
        let elements: SelectionPanelEntries = [];
        if (variantsRef.value) {
            elements.push({ type: 'No-Highlight' as const });
            const mappedResult = variantsRef.value.map((v) => ({
                type: 'Existing-Allocation-Variant' as const,
                id: v.id,
                index: v.index,
                variantName: v.name,
                isVisible: visibleVariantIds.value.includes(v.id),
                isMainVariant: v.isCurrent,
                status: v.status,
                linkTarget: getLinkTarget(allocationType, toValue(objectId), v.index),
                hasAllocations: v.allocationsTotalCount > 0,
                hasAllocationsInRange: v.allocations.nodes.length > 0,
            }));
            // splice mainVariant out and put it back in to be at the first place
            const mainVariantIndex = mappedResult.findIndex((v) => v.isMainVariant);
            const mainVariant = mappedResult.splice(mainVariantIndex, 1);
            elements.unshift(mainVariant[0]);
            elements = elements.concat(mappedResult);
        }
        return elements;
    });

    const visibleVariantsWithAllocations = computed(() =>
        selectionPanelVariants.value
            .filter(
                (variant): variant is ExistingAllocationVariantEntry => variant.type === 'Existing-Allocation-Variant',
            )
            .filter((variant) => variant.isVisible)
            .filter((variant) => variant.hasAllocations),
    );

    function setVisibleRouteQueryParam(indexes: number[]) {
        const defaultVisibles = isArrayEqual(
            indexes.map((i) => i.toString()),
            defaultActiveVariants.value.map((v) => v.index.toString()),
        );
        if (indexes.length > 0 && !defaultVisibles) {
            visible.value = indexes.join(',');
        } else {
            visible.value = '';
        }
    }

    function setVisibleVariantsByIds(value: string[]) {
        visibleVariantIds.value = value;
        const indexes = getVisibleIndexesFromIds(value, toValue(variantsRef));
        setVisibleRouteQueryParam(indexes);
    }

    function setVisibleVariantsByIndexes(indexes: number[]) {
        const ids = getVisibleIdsFromIndexes(indexes, toValue(variantsRef));
        visibleVariantIds.value = ids;
        setVisibleRouteQueryParam(indexes);
    }

    function updateVisibility(params: { allocationVariantId: string; isVisible: boolean }) {
        const { allocationVariantId, isVisible } = params;
        if (isVisible === false && visibleVariantIds.value.includes(allocationVariantId)) {
            visibleVariantIds.value = visibleVariantIds.value.filter((v) => v !== allocationVariantId);
        }
        if (isVisible === true && !visibleVariantIds.value.includes(allocationVariantId)) {
            visibleVariantIds.value = [...visibleVariantIds.value, allocationVariantId];
        }
        const indexes = getVisibleIndexesFromIds(visibleVariantIds.value, toValue(variantsRef));
        setVisibleRouteQueryParam(indexes);
    }

    function updateTransparenciesOfVariantsRef() {
        const variantsWithUpdatedTransparency = variantsRef.value.map((v) => {
            return {
                ...v,
                allocations: {
                    nodes: v.allocations.nodes.map((n) => ({
                        ...n,
                        variant: getTransparencyVariant(highlightedVariantId.value, v.id, v.isCurrent),
                    })),
                },
            };
        });
        setVariantsRef(variantsWithUpdatedTransparency);
    }

    function setHighlightedVariantIdById(id: string | undefined) {
        if (id && id !== 'No-Highlight-Id') {
            highlightedVariantId.value = id;
            updateVisibility({ allocationVariantId: id, isVisible: true });
            const variant = getVariantById(id, variantsRef.value);
            if (variant) {
                highlight.value = variant.index.toString();
            }
        } else {
            highlightedVariantId.value = DEFAULT_HIGHLIGHT_VALUE;
            highlight.value = DEFAULT_HIGHLIGHT_VALUE;
        }
        updateTransparenciesOfVariantsRef();
    }

    function setHighlightedVariantIdByIndex(index: number) {
        if (index > 0) {
            const variant = getVariantByIndex(index, variantsRef.value);
            if (variant) {
                highlightedVariantId.value = variant.id;
                updateVisibility({ allocationVariantId: variant.id, isVisible: true });
                highlight.value = variant.index.toString();
                updateTransparenciesOfVariantsRef();
            }
        }
    }

    function onDeleteFilter() {
        highlightedVariantId.value = DEFAULT_HIGHLIGHT_VALUE;
        visibleVariantIds.value =
            variantsRef.value.filter((v) => ACTIVE_STATES.includes(v.status)).map((v) => v.id) ?? [];
        visible.value = '';
        highlight.value = DEFAULT_HIGHLIGHT_VALUE;
    }

    return {
        dropDownStatus,
        visibleVariantIds,
        defaultActiveVariants,
        editableVariant,
        editableVariantLabel,
        highlightedVariantId,
        highlightedVariant,
        mappedCurrentVariant,
        selectionPanelVariants,
        setVisibleVariantsByIds,
        setVisibleVariantsByIndexes,
        setHighlightedVariantIdByIndex,
        setHighlightedVariantIdById,
        onDeleteFilter,
        updateVisibility,
    };
}
